import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Heart, Sprout, Star, MailOpen, Users, Target, NotebookPen, Globe, Headphones, Compass,
  CheckCircle2, ArrowLeft, PartyPopper, Music, Feather, ChevronDown, ChevronUp, ImagePlus, Loader2,
} from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const KEY_UI = [
  { id: 'coeur', num: 1, icon: Heart },
  { id: 'sagesse', num: 2, icon: Sprout },
  { id: 'phrase', num: 3, icon: Star },
  { id: 'mentor', num: 4, icon: MailOpen },
  { id: 'dialogue', num: 5, icon: Users },
  { id: 'mission', num: 6, icon: Target },
  { id: 'carnet', num: 7, icon: NotebookPen },
  { id: 'giveback', num: 8, icon: Globe },
  { id: 'aventure', num: 9, icon: Headphones },
  { id: 'projection', num: 10, icon: Compass },
];

const Lines = ({ lines }) => (
  <div className="space-y-2">
    {(lines || []).filter(Boolean).map((l, i) => (
      <p key={i} className="text-base text-brume leading-relaxed">{l}</p>
    ))}
  </div>
);

export default function ChapterPage() {
  const { number } = useParams();
  const [chapter, setChapter] = useState(null);
  const [myKeys, setMyKeys] = useState({});
  const [error, setError] = useState(null);
  const [sel, setSel] = useState({ coeur: [], sagesse: [] });
  const [texts, setTexts] = useState({ carnet: '', projection: '' });
  const [completed, setCompleted] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    api.get(`/chapters/${number}`)
      .then((r) => {
        setChapter(r.data);
        setCompleted(!!r.data.completed);
        const mk = r.data.my_keys || {};
        setMyKeys(mk);
        setSel({
          coeur: mk.coeur?.data?.emotions || [],
          sagesse: mk.sagesse?.data?.lessons || [],
        });
        setTexts({
          carnet: mk.carnet?.data?.text || '',
          projection: mk.projection?.data?.text || '',
        });
        const photoPath = mk.giveback?.data?.photo_path;
        if (photoPath) {
          api.post('/files/sign', { path: photoPath })
            .then((s) => api.get(s.data.url, { responseType: 'blob' }))
            .then((r) => setPhotoUrl(URL.createObjectURL(r.data)))
            .catch(() => {});
        }
      })
      .catch((err) => setError(err.response?.data?.detail || t('chapter.notFound')));
  }, [number]);

  const save = async (key, value, data, msg) => {
    try {
      await api.post(`/chapters/${number}/keys/${key}`, { value, data });
      setMyKeys((m) => ({ ...m, [key]: { value, data } }));
      toast.success(msg || t('chapter.keyValidated', { value }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const toggleSel = (key, item) => {
    setSel((s) => ({
      ...s,
      [key]: s[key].includes(item) ? s[key].filter((x) => x !== item) : [...s[key], item],
    }));
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/chapters/${number}/keys/giveback/photo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoUrl(URL.createObjectURL(file));
      setMyKeys((m) => ({
        ...m,
        giveback: { value: 10, data: { ...(m.giveback?.data || {}), status: 'done', photo_path: data.photo_path } },
      }));
      toast.success(t('keys.giveback.photoToast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('keys.giveback.uploadError'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const complete = async () => {
    try {
      const { data } = await api.post(`/chapters/${number}/complete`);
      setCompleted(true);
      toast.success(data.unlocked_next ? t('chapter.completeNext') : t('chapter.completeDone'));
      setTimeout(() => navigate('/tableau-de-bord'), 900);
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  if (error) {
    return (
      <div className="text-center py-20" data-testid="chapter-error">
        <p className="text-xl font-bold text-brume">{error}</p>
        <Button onClick={() => navigate('/tableau-de-bord')} className="mt-6 rounded-full bg-or-400 text-nuit-950 font-bold" data-testid="chapter-back-button">{t('common.back')}</Button>
      </div>
    );
  }
  if (!chapter) return <p className="text-brume py-20" data-testid="chapter-loading">{t('common.loading')}</p>;

  const k = chapter.keys || {};
  const score = Object.values(myKeys).reduce((acc, x) => acc + (x.value || 0), 0);

  const KeyHeader = ({ ui }) => (
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-xl font-semibold text-creme flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-or-400/15 flex items-center justify-center shrink-0">
          <ui.icon className="w-5 h-5 text-or-400" strokeWidth={2.5} />
        </span>
        <span><span className="text-or-400 mr-2">{ui.num}.</span>{t(`keys.${ui.id}.title`)}</span>
      </h2>
      {(myKeys[ui.id]?.value || 0) >= 10 ? (
        <span className="text-xs font-bold text-or-300 bg-or-400/15 px-3 py-1.5 rounded-full flex items-center gap-1 shrink-0"
          data-testid={`key-done-${ui.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5" /> +10
        </span>
      ) : (myKeys[ui.id]?.value || 0) > 0 ? (
        <span className="text-xs font-bold text-brume bg-nuit-900 px-3 py-1.5 rounded-full shrink-0">+{myKeys[ui.id].value}</span>
      ) : null}
    </div>
  );

  const renderKey = (ui) => {
    const content = {
      coeur: (
        <>
          <Lines lines={k.coeur?.intro} />
          {k.coeur?.emotions?.length > 0 && (
            <>
              <p className="text-sm font-bold text-creme mt-4 mb-3">{t('keys.coeur.question')}</p>
              <div className="flex flex-wrap gap-2">
                {k.coeur.emotions.map((e) => (
                  <button key={e} data-testid={`emotion-${e}`} onClick={() => toggleSel('coeur', e)}
                    className={`px-4 py-2 rounded-full text-sm font-bold border transition-all
                      ${sel.coeur.includes(e) ? 'bg-or-400 text-nuit-950 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600 hover:border-or-400/50'}`}>
                    {e}
                  </button>
                ))}
              </div>
            </>
          )}
          <Lines lines={k.coeur?.learn} />
          <Button data-testid="key-action-coeur" disabled={!sel.coeur.length}
            onClick={() => save('coeur', 10, { emotions: sel.coeur }, t('keys.coeur.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.coeur.action')}
          </Button>
        </>
      ),
      sagesse: (
        <>
          <p className="text-sm font-bold text-creme mb-3">{t('keys.sagesse.question')}</p>
          <div className="space-y-2">
            {(k.sagesse?.lessons || []).map((l, i) => (
              <button key={i} data-testid={`lesson-${i}`} onClick={() => toggleSel('sagesse', i)}
                className={`w-full text-left rounded-2xl px-5 py-3 text-sm font-semibold border transition-all
                  ${sel.sagesse.includes(i) ? 'bg-or-400/20 text-or-200 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600 hover:border-or-400/50'}`}>
                {l}
              </button>
            ))}
          </div>
          <Button data-testid="key-action-sagesse" disabled={!sel.sagesse.length}
            onClick={() => save('sagesse', 10, { lessons: sel.sagesse }, t('keys.sagesse.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.sagesse.action')}
          </Button>
        </>
      ),
      phrase: (
        <>
          {k.phrase?.text && (
            <blockquote className="border-l-4 border-or-400 pl-5 py-2 font-fredoka text-xl text-creme italic leading-relaxed">
              « {k.phrase.text} »
            </blockquote>
          )}
          <Button data-testid="key-action-phrase"
            onClick={() => save('phrase', 10, { text: k.phrase?.text || '' }, t('keys.phrase.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.phrase.action')}
          </Button>
        </>
      ),
      mentor: (
        <>
          {k.mentor?.name && (
            <p className="text-sm font-bold text-or-300 mb-3">{t('keys.mentor.letterFrom', { name: k.mentor.name })}</p>
          )}
          <div className="bg-nuit-900 border-l-4 border-or-400/50 rounded-2xl p-5">
            <Lines lines={k.mentor?.letter} />
          </div>
          <Button data-testid="key-action-mentor"
            onClick={() => save('mentor', 10, {}, t('keys.mentor.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.mentor.action')}
          </Button>
        </>
      ),
      dialogue: (
        <>
          <Lines lines={k.dialogue?.lines} />
          <Button data-testid="key-action-dialogue"
            onClick={() => save('dialogue', 10, { status: 'done' }, t('keys.dialogue.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.dialogue.action')}
          </Button>
        </>
      ),
      mission: (
        <>
          <Lines lines={k.mission?.lines} />
          <div className="flex flex-wrap gap-3 mt-5">
            <Button data-testid="key-action-mission-started" variant="outline"
              onClick={() => save('mission', 5, { status: 'started' }, t('keys.mission.startedToast'))}
              className="rounded-full border-2 border-or-400/60 text-or-300 font-bold bg-transparent hover:bg-or-400/10">
              {t('keys.mission.started')}
            </Button>
            <Button data-testid="key-action-mission-done"
              onClick={() => save('mission', 10, { status: 'done' }, t('keys.mission.doneToast'))}
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
              {t('keys.mission.done')}
            </Button>
          </div>
        </>
      ),
      carnet: (
        <>
          <div className="diary-lines rounded-2xl p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">{t('keys.carnet.writes')}</p>
            {(k.carnet?.note || []).map((l, i) => (
              <p key={i} className="font-caveat text-2xl text-slate-800" style={{ lineHeight: '36px' }}>{l}</p>
            ))}
          </div>
          <p className="text-sm font-bold text-creme mt-4 mb-2">{k.carnet?.prompt || t('keys.carnet.promptDefault')}</p>
          <Textarea data-testid="carnet-input" rows={3} value={texts.carnet}
            onChange={(e) => setTexts((tx) => ({ ...tx, carnet: e.target.value }))}
            placeholder={t('keys.carnet.placeholder')}
            className="rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
          <p className="text-xs text-brume mt-2">{t('keys.carnet.encrypted')}</p>
          <Button data-testid="key-action-carnet" disabled={!texts.carnet.trim()}
            onClick={() => save('carnet', 10, { text: texts.carnet }, t('keys.carnet.toast'))}
            className="mt-4 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.carnet.action')}
          </Button>
        </>
      ),
      giveback: (
        <>
          <Lines lines={k.giveback?.lines} />
          {photoUrl && (
            <img src={photoUrl} alt={t('keys.giveback.photoAlt')} data-testid="giveback-photo"
              className="mt-4 rounded-2xl h-40 w-auto object-cover ring-2 ring-or-400/40" />
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            data-testid="giveback-photo-input" onChange={uploadPhoto} />
          <div className="flex flex-wrap gap-3 mt-5">
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="giveback-photo-button"
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" strokeWidth={2.5} />}
              {photoUrl ? t('keys.giveback.changePhoto') : t('keys.giveback.addPhoto')}
            </Button>
            {!photoUrl && (
              <Button data-testid="key-action-giveback" variant="outline"
                onClick={() => save('giveback', 10, { status: 'done' }, t('keys.giveback.toast'))}
                className="rounded-full border-2 border-or-400/60 text-or-300 font-bold bg-transparent hover:bg-or-400/10">
                {t('keys.giveback.withoutPhoto')}
              </Button>
            )}
          </div>
        </>
      ),
      aventure: (
        <>
          <Lines lines={k.aventure?.lines} />
          <Button data-testid="key-action-aventure"
            onClick={() => save('aventure', 10, {}, t('keys.aventure.toast'))}
            className="mt-5 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.aventure.action')}
          </Button>
        </>
      ),
      projection: (
        <>
          <Lines lines={k.projection?.lines} />
          <Textarea data-testid="projection-input" rows={2} value={texts.projection}
            onChange={(e) => setTexts((tx) => ({ ...tx, projection: e.target.value }))}
            placeholder={t('keys.projection.placeholder')}
            className="mt-3 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
          <Button data-testid="key-action-projection" disabled={!texts.projection.trim()}
            onClick={() => save('projection', 10, { text: texts.projection }, t('keys.projection.toast'))}
            className="mt-4 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {t('keys.projection.action')}
          </Button>
        </>
      ),
    }[ui.id];

    return (
      <motion.section key={ui.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }} transition={spring}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8"
        data-testid={`key-block-${ui.id}`}>
        <KeyHeader ui={ui} />
        {content}
      </motion.section>
    );
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="chapter-page">
      <button onClick={() => navigate('/tableau-de-bord')} data-testid="chapter-back-link"
        className="flex items-center gap-2 text-or-400 font-bold mb-8 hover:underline">
        <ArrowLeft className="w-4 h-4" strokeWidth={3} /> {t('nav.chapters')}
      </button>

      <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}
        className="relative rounded-3xl overflow-hidden gold-glow ring-1 ring-or-400/30">
        <img src="/images/eliano_stade_clean.jpg" alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-r from-nuit-950/85 via-nuit-950/70 to-nuit-950/45" />
        <div className="relative p-8 sm:p-10">
        <p className="text-or-400 font-bold tracking-widest text-xs uppercase">{chapter.act}</p>
        <p className="font-fredoka text-brume mt-4 text-lg">{t('common.chapterN', { n: chapter.number }).toUpperCase()}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-1">{chapter.title}</h1>
        {chapter.subtitle && (
          <p className="text-lg text-or-300 italic mt-4 leading-relaxed">« {chapter.subtitle} »</p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-nuit-900/80 text-or-300 border border-or-400/40"
            data-testid="chapter-score">
            {t('chapter.score', { score })}
          </span>
          {completed && (
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-or-400 text-nuit-950 flex items-center gap-1" data-testid="chapter-done-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t('chapter.done')}
            </span>
          )}
        </div>
        <p className="text-sm text-brume mt-4">{t('chapter.intro')}</p>
        </div>
      </motion.header>

      <div className="space-y-6 mt-8">
        {KEY_UI.map(renderKey)}
      </div>

      {chapter.closing_word && (
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-8 bg-or-400 rounded-3xl p-8 text-center" data-testid="closing-word">
          <Feather className="w-6 h-6 text-nuit-900 mx-auto mb-3" strokeWidth={2.5} />
          <p className="text-xs font-bold uppercase tracking-widest text-nuit-800 mb-2">{t('chapter.closingWord')}</p>
          <p className="font-fredoka text-2xl text-nuit-950 leading-snug">« {chapter.closing_word} »</p>
        </motion.div>
      )}

      {chapter.song?.title && (
        <div className="mt-6 bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8" data-testid="song-card">
          <button onClick={() => setShowLyrics((v) => !v)} data-testid="song-toggle"
            className="w-full flex items-center justify-between gap-3 text-left">
            <h2 className="text-xl font-semibold text-creme flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-or-400/15 flex items-center justify-center">
                <Music className="w-5 h-5 text-or-400" strokeWidth={2.5} />
              </span>
              {t('chapter.song', { title: chapter.song.title })}
            </h2>
            {showLyrics ? <ChevronUp className="w-5 h-5 text-brume" /> : <ChevronDown className="w-5 h-5 text-brume" />}
          </button>
          {chapter.song.style && <p className="text-xs text-brume mt-2 italic">{chapter.song.style}</p>}
          {showLyrics && (
            <div className="mt-4 space-y-3" data-testid="song-lyrics">
              {chapter.song.lyrics.map((l, i) => (
                <p key={i} className="text-sm text-brume whitespace-pre-line leading-relaxed">{l}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {chapter.author_note && (
        <div className="mt-6 bg-nuit-800/60 border border-nuit-700 rounded-3xl p-6 sm:p-8" data-testid="author-note">
          <p className="text-xs font-bold uppercase tracking-widest text-brume mb-3">{t('chapter.authorNote')}</p>
          <p className="text-base text-brume leading-relaxed">{chapter.author_note}</p>
        </div>
      )}

      {!completed && (
        <div className="mt-12 mb-8 text-center">
          <Button onClick={complete} data-testid="chapter-complete-button"
            className="h-16 rounded-full px-12 bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-lg shadow-lg hover:-translate-y-0.5 transition-transform gap-3">
            <PartyPopper className="w-6 h-6" strokeWidth={2.5} />
            {t('chapter.completeBtn')}
          </Button>
        </div>
      )}
    </div>
  );
}
